/**
 * WorkflowStepper — top-of-page 7-stage progress indicator.
 *
 * Matches the original QuestionnaireStepper step names and count:
 *   1. Information Retrieval
 *   2. Document Upload
 *   3. Provider Assignment
 *   4. Platform Clarification
 *   5. Draft Evaluation
 *   6. Payment Completion
 *   7. Service Completion
 *
 * Responsive:
 *   - Mobile (< sm)  : circle nodes + short labels, connector lines compress
 *   - Desktop (≥ sm) : circle nodes + full labels
 */
import * as React from 'react';
import {
  Info, Upload, Users, MessageCircle,
  FileCheck, Wallet, CheckCircle2,
} from 'lucide-react';
import { STEPPER_STEPS, deriveStepperIndex } from '../constants';
import { PALETTE_FOREST, PALETTE_AMBER } from '../tokens';
import type { CustomerServiceRequestWorkflowState } from '@/api/service-requests';

/** One icon per stepper step — must stay in sync with STEPPER_STEPS order. */
const STEP_ICONS = [Info, Upload, Users, MessageCircle, FileCheck, Wallet, CheckCircle2] as const;

// ── Types ─────────────────────────────────────────────────────────────────────

interface WorkflowStepperProps {
  workflowState: CustomerServiceRequestWorkflowState | string | null | undefined;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function WorkflowStepper({ workflowState }: WorkflowStepperProps) {
  const currentStep = deriveStepperIndex(workflowState);

  return (
    <div className="w-full px-2 py-4 no-scrollbar overflow-x-auto">
      <div className="flex items-center justify-between min-w-[560px] max-w-5xl mx-auto">
        {STEPPER_STEPS.map((step, idx) => {
          const stepNum = idx + 1;
          const done    = currentStep > stepNum;
          const active  = currentStep === stepNum;
          const Icon    = STEP_ICONS[idx]!;
          const isLast  = idx === STEPPER_STEPS.length - 1;

          return (
            <React.Fragment key={step.label}>
              {/* ── Step node ──────────────────────────────────────────────── */}
              <div className="flex flex-col items-center gap-1.5 shrink-0">
                {/* Outer halo — pulses gently while the step is active. */}
                <div className="relative">
                  {active && (
                    <span
                      aria-hidden
                      className="absolute inset-0 rounded-full animate-ping"
                      style={{ backgroundColor: `${PALETTE_AMBER}55` }}
                    />
                  )}
                  <div
                    className="relative h-8 w-8 sm:h-9 sm:w-9 rounded-full flex items-center justify-center transition-all duration-300 shrink-0"
                    style={{
                      backgroundColor: done   ? PALETTE_FOREST
                                     : active ? PALETTE_AMBER
                                     : 'hsl(var(--muted))',
                      color: done || active ? '#fff' : 'hsl(var(--muted-foreground))',
                      boxShadow: active
                        ? `0 0 0 4px ${PALETTE_AMBER}33, 0 4px 14px ${PALETTE_AMBER}44`
                        : done
                          ? `0 2px 8px ${PALETTE_FOREST}33`
                          : undefined,
                      transform: active ? 'scale(1.06)' : 'scale(1)',
                    }}
                    aria-label={`Step ${stepNum}: ${step.label}${done ? ' (completed)' : active ? ' (current)' : ''}`}
                  >
                    {done
                      ? <CheckCircle2 className="h-4 w-4 animate-fade-in" aria-hidden />
                      : <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
                    }
                  </div>
                </div>

                {/* Label — short on mobile, full on sm+ */}
                <span
                  className="text-[9px] sm:text-[10px] font-semibold text-center leading-tight max-w-[52px] sm:max-w-[72px] transition-all duration-300"
                  style={{
                    color: active || done ? PALETTE_FOREST : 'hsl(var(--muted-foreground))',
                    opacity: active || done ? 1 : 0.6,
                  }}
                >
                  <span className="sm:hidden">{step.shortLabel}</span>
                  <span className="hidden sm:inline">{step.label}</span>
                </span>
              </div>

              {/* ── Connector line ─────────────────────────────────────────── */}
              {!isLast && (
                <div
                  className="flex-1 h-0.5 mx-1 rounded-full overflow-hidden"
                  style={{ backgroundColor: 'hsl(var(--border))' }}
                >
                  <div
                    className="h-full transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] rounded-full"
                    style={{
                      width: done ? '100%' : active ? '50%' : '0%',
                      backgroundColor: PALETTE_FOREST,
                    }}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
